import { useState } from 'react';
import { Filter } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Checkbox,
} from '@predoc/cow';
import { DatePickerInput } from '@/components/DatePickerInput';
import { STATUS_CONFIG } from '../../constants';
import {
  PatientStatus,
  FilterState,
  PatientFilterProps,
  StatusColumnProps,
  RETRIEVAL_STATUSES,
  UPLOAD_STATUSES,
  FACESHEET_STATUSES,
} from '../../types';

const StatusColumn = ({
  title,
  statuses,
  selectedStatuses,
  onStatusChange,
  onSelectAll,
}: StatusColumnProps) => {
  const allSelected = statuses.every((status) =>
    selectedStatuses.includes(status),
  );
  const someSelected = statuses.some((status) =>
    selectedStatuses.includes(status),
  );

  return (
    <div className="space-y-3">
      <Checkbox
        checked={allSelected}
        onCheckedChange={(checked: boolean) => onSelectAll(statuses, checked)}
        className={
          someSelected && !allSelected
            ? 'data-[state=indeterminate]:border-primary'
            : ''
        }
        label={
          <span className="text-foreground text-sm font-medium">{title}</span>
        }
      />
      <div className="space-y-2 pl-6">
        {statuses.map((status) => {
          const statusConfig = STATUS_CONFIG[status];
          if (!statusConfig) return null;

          const { icon: IconComponent, filterLabel } = statusConfig;
          const isSelected = selectedStatuses.includes(status);

          return (
            <Checkbox
              key={status}
              checked={isSelected}
              onCheckedChange={(checked: boolean) =>
                onStatusChange(status, checked)
              }
              label={
                <div className="inline-flex items-center">
                  <IconComponent aria-hidden="true" className="mr-1 size-4" />
                  <span>{filterLabel}</span>
                </div>
              }
            />
          );
        })}
      </div>
    </div>
  );
};

const DATE_RANGE_ERROR = 'End date must be after or equal to start date.';

const isInvalidDateRange = (from?: Date, to?: Date) =>
  !!from && !!to && from > to;

export const PatientFilter = ({ onFiltersChange }: PatientFilterProps) => {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    modifiedFrom: undefined,
    modifiedTo: new Date(),
    statuses: [],
  });
  const [dateError, setDateError] = useState<string | undefined>(undefined);

  const handleDateChange = (
    field: 'modifiedFrom' | 'modifiedTo',
    date: Date | undefined,
  ) => {
    const newFilters = {
      ...filters,
      [field]: date,
    };
    setFilters(newFilters);

    if (isInvalidDateRange(newFilters.modifiedFrom, newFilters.modifiedTo)) {
      setDateError(DATE_RANGE_ERROR);
      return;
    }

    setDateError(undefined);
    onFiltersChange?.(newFilters);
  };

  const handleStatusChange = (status: PatientStatus, checked: boolean) => {
    const newStatuses = checked
      ? [...filters.statuses, status]
      : filters.statuses.filter((s) => s !== status);

    const newFilters = {
      ...filters,
      statuses: newStatuses,
    };

    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleSelectAll = (statuses: PatientStatus[], checked: boolean) => {
    const otherStatuses = filters.statuses.filter(
      (s) => !statuses.includes(s as PatientStatus),
    );
    const newStatuses = checked
      ? [...otherStatuses, ...statuses]
      : otherStatuses;

    const newFilters = {
      ...filters,
      statuses: newStatuses,
    };

    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleClear = () => {
    const clearedFilters: FilterState = {
      modifiedFrom: undefined,
      modifiedTo: new Date(),
      statuses: [],
    };
    setFilters(clearedFilters);
    setDateError(undefined);
    onFiltersChange?.(clearedFilters);
  };

  const hasActiveFilters =
    !!filters.modifiedFrom || filters.statuses.length > 0;

  return (
    <div className="flex items-center gap-2">
      {hasActiveFilters && (
        <Button variant="ghost" onClick={handleClear}>
          Clear
        </Button>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={hasActiveFilters ? 'border-primary bg-primary/5' : ''}
          >
            <Filter className="size-4" />
            Filters{hasActiveFilters && ' (Applied)'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-6" align="end">
          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-foreground text-sm font-medium">
                Date Added to Organization
              </h3>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-muted-foreground mb-1 block text-xs">
                    Start Date
                  </label>
                  <DatePickerInput
                    value={filters.modifiedFrom}
                    onChange={(date) => handleDateChange('modifiedFrom', date)}
                    placeholder="MM/DD/YYYY"
                    disableDates="future"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-muted-foreground mb-1 block text-xs">
                    End Date
                  </label>
                  <DatePickerInput
                    value={filters.modifiedTo}
                    onChange={(date) => handleDateChange('modifiedTo', date)}
                    placeholder="MM/DD/YYYY"
                    disableDates="future"
                  />
                </div>
              </div>
              {dateError && (
                <p className="text-destructive text-xs">{dateError}</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-6">
              <StatusColumn
                title="Retrieval Status"
                statuses={RETRIEVAL_STATUSES}
                selectedStatuses={filters.statuses}
                onStatusChange={handleStatusChange}
                onSelectAll={handleSelectAll}
              />
              <StatusColumn
                title="Upload Status"
                statuses={UPLOAD_STATUSES}
                selectedStatuses={filters.statuses}
                onStatusChange={handleStatusChange}
                onSelectAll={handleSelectAll}
              />
              <StatusColumn
                title="Facesheet Status"
                statuses={FACESHEET_STATUSES}
                selectedStatuses={filters.statuses}
                onStatusChange={handleStatusChange}
                onSelectAll={handleSelectAll}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
